import { Language as LanguageIcon, Close as CloseIcon } from '@mui/icons-material';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    TextField,
    TextFieldProps,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import React, { useState, useMemo } from 'react';
import ApplicationButton from '../ApplicationButton';
import { useMicrospecThemeLanguageContext } from '../../Themes';

interface LanguageConfig {
    /**
     * Preferred/default language code
     */
    preferred: string;

    /**
     * Array of supported language codes
     */
    supported: string[];
}

interface LocalizedValue {
    [languageCode: string]: string;
}

interface LocalizedTextFieldProps extends Omit<TextFieldProps, 'value' | 'onChange'> {
    /**
     * Language configuration with preferred and supported languages
     * @example
     * {
     *   preferred: "en-US",
     *   supported: ["en-US", "ar-SA", "he-IL"]
     * }
     */
    languageConfig: LanguageConfig;

    /**
     * Current localized value as an object with language codes as keys
     * e.g., { "ar-SA": "قيمة", "en-US": "Value" }
     * Can also accept a plain string which will be treated as the preferred language value
     */
    value: string | LocalizedValue;

    /**
     * Callback when value changes
     */
    onChange: (value: LocalizedValue | string) => void;

    /**
     * Whether the input is multiline (textarea)
     */
    multiline?: boolean;

    /**
     * Number of rows for multiline input
     */
    rows?: number;

    /**
     * Maximum number of characters allowed
     */
    maxLength?: number;
}

/**
 * LocalizedTextInput Component
 * 
 * A text input that supports multiple languages. When more than one language is provided,
 * it displays a language icon that opens a dialog to edit all localizations.
 * 
 * @example
 * ```tsx
 * const [valConfig={{
 *     preferred: "en-US",
 *     supported: ["en-US", "ar-SA", "he-IL"]
 *   }}
 *   value={value}
 *   onChange={setValue}
 *   label="Product Name"
 * />
 * ```
 */
const LocalizedTextField: React.FC<LocalizedTextFieldProps> = ({
    languageConfig,
    value = {},
    onChange,
    multiline = false,
    rows = 4,
    label,
    helperText,
    disabled,
    required,
    maxLength,
    ...textFieldProps
}) => {
    const theme = useTheme();
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const t = localizationProvider.translate;

    const [dialogOpen, setDialogOpen] = useState(false);
    const [localizedValues, setLocalizedValues] = useState<LocalizedValue>({});

    // Determine the primary language to display in the main input
    const primaryLanguage = useMemo(() => {
        const { preferred, supported } = languageConfig;
        if (preferred && supported.includes(preferred)) {
            return preferred;
        }
        return supported[0] || '';
    }, [languageConfig]);

    // Normalize value to always be an object
    const normalizedValue = useMemo<LocalizedValue>(() => {
        if (typeof value === 'string') {
            // If value is a string, treat it as the preferred language value
            return value ? { [primaryLanguage]: value } : {};
        }
        return value || {};
    }, [value, primaryLanguage]);

    // Check if we have multiple languages
    const isMultilingual = languageConfig.supported.length > 1;

    // Get language name from code
    // Get language name from code
    const getLanguageName = (code: string): string => {
        const languageNames: { [key: string]: string } = localizationProvider.supportedLanguagesMap;
        return languageNames[code] || code;
    };

    // Get character count display
    const getCharacterCount = (text: string): string => {
        if (!maxLength) return '';
        return `${text.length}/${maxLength}`;
    };

    // Get helper text with character count
    const getHelperText = (): React.ReactNode => {
        const currentValue = normalizedValue[primaryLanguage] || '';
        const charCount = maxLength ? getCharacterCount(currentValue) : '';

        if (helperText && charCount) {
            return `${helperText} (${charCount})`;
        }
        if (charCount) {
            return charCount;
        }
        if (helperText) {
            return helperText;
        }
        if (isMultilingual && !disabled) {
            return t({
                text: 'localizedInputClickIconToLocalize',
                type: 'Internal'
            });
        }
        return undefined;
    };

    // Handle main input change
    const handleMainInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = {
            ...normalizedValue,
            [primaryLanguage]: event.target.value
        };
        onChange(newValue);
    };

    // Handle opening the localization dialog
    const handleOpenDialog = () => {
        setLocalizedValues({ ...normalizedValue });
        setDialogOpen(true);
    };

    // Handle localization value change in dialog
    const handleLocalizedChange = (languageCode: string, newValue: string) => {
        setLocalizedValues(prev => ({
            ...prev,
            [languageCode]: newValue
        }));
    };

    // Handle saving localizations
    const handleSave = () => {
        onChange(localizedValues);
        setDialogOpen(false);
    };

    // Handle cancel
    const handleCancel = () => {
        setLocalizedValues({ ...normalizedValue });
        setDialogOpen(false);
    };

    return (
        <>
            <TextField
                {...textFieldProps}
                label={label}
                value={normalizedValue[primaryLanguage] || ''}
                onChange={handleMainInputChange}
                disabled={disabled}
                required={required}
                multiline={multiline}
                rows={multiline ? rows : undefined}
                helperText={getHelperText()}
                inputProps={{
                    ...textFieldProps.inputProps,
                    maxLength: maxLength
                }}
                InputProps={{
                    ...textFieldProps.InputProps,
                    endAdornment: isMultilingual && !disabled ? (
                        <Tooltip title={t({
                            type: 'Internal',
                            text: 'localizedInputManageLocalizations'
                        })}>
                            <IconButton
                                onClick={handleOpenDialog}
                                edge="end"
                                color="primary"
                                sx={{ mr: -1 }}
                            >
                                <LanguageIcon />
                            </IconButton>
                        </Tooltip>
                    ) : textFieldProps.InputProps?.endAdornment
                }}
                fullWidth
            />

            {/* Localization Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCancel}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        maxHeight: '80vh'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        color: 'white',
                        py: 2
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <LanguageIcon />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {t({
                                text: 'localizedInputManageLocalizations',
                                type: 'Internal'
                            })}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleCancel}
                        sx={{ color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {label && `${label} - `}
                        {t({
                            text: 'localizedInputEditAllLanguages',
                            type: 'Internal'
                        })}
                    </Typography>

                    <Stack spacing={3}>
                        {languageConfig.supported
                            .sort((languageConfig1, languageConfig2) => languageConfig1 === languageConfig.preferred ? -1 : languageConfig2 === languageConfig.preferred ? 1 : 0)
                            .map((languageCode, index) => (
                                <Box key={languageCode}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: theme.palette.primary.main
                                            }}
                                        >
                                            {getLanguageName(languageCode)}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'text.secondary',
                                                bgcolor: 'action.hover',
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: 1
                                            }}
                                        >
                                            {languageCode}
                                        </Typography>
                                        {languageCode === languageConfig.preferred && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'primary.main',
                                                    bgcolor: 'primary.light',
                                                    px: 1,
                                                    py: 0.25,
                                                    borderRadius: 1,
                                                    fontWeight: 600
                                                }}
                                            >
                                                {t({
                                                    text: 'localizedInputPreferred',
                                                    type: 'Internal'
                                                })}
                                            </Typography>
                                        )}
                                    </Box>
                                    <TextField
                                        fullWidth
                                        value={localizedValues[languageCode] || ''}
                                        onChange={(e) => handleLocalizedChange(languageCode, e.target.value)}
                                        multiline={multiline}
                                        rows={multiline ? rows : undefined}
                                        placeholder={`${t({
                                            text: 'localizedInputEnterValue',
                                            type: 'Internal'
                                        })} (${getLanguageName(languageCode)})`}
                                        variant="outlined"
                                        helperText={maxLength ? getCharacterCount(localizedValues[languageCode] || '') : undefined}
                                        inputProps={{
                                            maxLength: maxLength
                                        }}
                                    />
                                    {index < languageConfig.supported.length - 1 && <Divider sx={{ mt: 2 }} />}
                                </Box>
                            ))}
                    </Stack>
                </DialogContent>

                <Divider />

                <DialogActions sx={{ p: 2, justifyContent: 'flex-end', gap: 1 }}>
                    <ApplicationButton
                        onClick={handleCancel}
                        sx={{ borderRadius: 2 }}
                    >
                        {t('common.cancel') || 'Cancel'}
                    </ApplicationButton>
                    <ApplicationButton
                        onClick={handleSave}
                        variant="contained"
                        sx={{
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
                            }
                        }}
                    >
                        {t('common.save') || 'Save'}
                    </ApplicationButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default LocalizedTextField;
